import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { produce } from 'immer';
import _ from 'lodash';

import {
  CleanupRemovedTicketData,
  GetTicketsSuccess,
  UpdateLocalTicketRelationships
} from '../tickets/tickets.action';
import { NormalizedTransaction } from 'src/app/models/normalized-models/normalized-transaction.model';
import { NormalizrHelper } from 'src/app/helpers';
import { MakePaymentSuccess } from 'src/app/store/payment/payment.action';
import { NormalizedObject } from 'src/app/models';
import { PatchTransactionSignature } from './transactions.action';

export interface TransactionsStateModel {
  transactions: NormalizedObject<NormalizedTransaction, number>;
}

export const TRANSACTIONS_STATE_TOKEN = new StateToken<TransactionsStateModel>(
  'transactions'
);

@State({
  name: TRANSACTIONS_STATE_TOKEN,
  defaults: {
    transactions: {
      byId: {},
      allIds: []
    }
  }
})
@Injectable()
export class TransactionsState {
  constructor() {}

  @Selector()
  static transactions(state: TransactionsStateModel) {
    return state.transactions;
  }

  @Action([GetTicketsSuccess, UpdateLocalTicketRelationships])
  getTicketsSuccess(
    ctx: StateContext<TransactionsStateModel>,
    payload: GetTicketsSuccess | UpdateLocalTicketRelationships
  ) {
    const normalizedTransactions = payload.entities.transactions;
    if (normalizedTransactions) {
      ctx.setState(
        produce((draft: TransactionsStateModel) => {
          draft.transactions = {
            byId: {
              ...draft.transactions.byId,
              ...normalizedTransactions
            },
            allIds: _.union(
              draft.transactions.allIds,
              Object.keys(normalizedTransactions).map((key) => +key)
            )
          };
        })
      );
    }
  }

  @Action(MakePaymentSuccess)
  makePaymentSuccess(
    ctx: StateContext<TransactionsStateModel>,
    { payment }: MakePaymentSuccess
  ) {
    const normalizeTransactions = NormalizrHelper.normalizeTransactions([
      payment.transaction
    ]).entities.transactions;

    ctx.setState(
      produce((draft) => {
        if (normalizeTransactions) {
          draft.transactions = {
            byId: {
              ...draft.transactions.byId,
              ...normalizeTransactions
            },
            allIds: _.union(
              draft.transactions.allIds,
              Object.keys(normalizeTransactions).map((key) => +key)
            )
          };
        }
      })
    );
  }

  /**
   * POS-972 On-Screen Signature.
   * `TransactionLookupService.patchTransactionSignature` is a raw HTTP call outside NGXS — this
   * action is dispatched afterwards so `selectedTicket.transactions[i].signature_status` (denormalized
   * off this state) reflects the resolved signature instead of staying stuck on 'awaiting'.
   */
  @Action(PatchTransactionSignature)
  patchTransactionSignature(
    { setState }: StateContext<TransactionsStateModel>,
    { transactionId, data }: PatchTransactionSignature
  ) {
    setState(
      produce((draft: TransactionsStateModel) => {
        const existing = draft.transactions.byId[transactionId];
        if (existing) {
          draft.transactions.byId[transactionId] = { ...existing, ...data };
        }
      })
    );
  }

  @Action(CleanupRemovedTicketData)
  cleanupTransactions(
    { setState }: StateContext<TransactionsStateModel>,
    { transactionIds }: CleanupRemovedTicketData
  ) {
    if (!transactionIds.length) {
      return;
    }

    setState(
      produce((draft) => {
        draft.transactions = {
          ...draft.transactions,
          byId: _.omit(draft.transactions.byId, transactionIds),
          allIds: _.difference(draft.transactions.allIds, transactionIds)
        };
      })
    );
  }
}
